import React, { useState, useEffect, useRef } from 'react';
import SuperPhilosophyTagline from '../../components/SuperPhilosophyTagline';
import QuoteTool from '../../components/QuoteTool';
import LaunchBanner from "../../components/LaunchBanner";

const PEOPartnership = () => {
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    Object.values(sectionRefs.current).forEach(ref => { if (ref) observer.observe(ref); });
    return () => observer.disconnect();
  }, []);

  const setSectionRef = (id) => (el) => { sectionRefs.current[id] = el; };
  const isVisible = (id) => visibleSections.has(id);
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const fadeIn = (id, delay = 0) => ({
    opacity: isVisible(id) ? 1 : 0,
    transform: isVisible(id) ? 'translateY(0)' : 'translateY(28px)',
    transition: `all 0.75s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`
  });

  const T = {
    pageBg:       '#f0f4f8',
    sectionAlt:   '#f8fafc',
    heroBg:       'linear-gradient(160deg, #1e3a5f 0%, #1a3352 45%, #162b45 100%)',
    heading:      '#1e293b',
    body:         '#475569',
    bodyLight:    '#64748b',
    accent:       '#4f46e5',
    accentDark:   '#4338ca',
    accentLight:  '#eef2ff',
    accentBorder: '#c7d2fe',
    green:        '#059669',
    greenLight:   '#ecfdf5',
    greenBorder:  '#a7f3d0',
    red:          '#ef4444',
    redLight:     '#fef2f2',
    redBorder:    '#fecaca',
    amber:        '#d97706',
    amberLight:   '#fef3c7',
    amberBorder:  '#fde68a',
    cardBg:       '#ffffff',
    cardBorder:   '#e2e8f0',
    navy:         '#1e3a5f',
    ctaBg:        'linear-gradient(180deg, #eef2ff 0%, #f0f4f8 60%, #ecfdf5 100%)',
  };

  const mobileCSS = `
    .peo-comp-wrapper { overflow-x: auto; }
    .peo-comp-header {
      display: grid;
      grid-template-columns: 1.6fr 1fr 1fr 1fr 1.2fr;
      gap: 0;
    }
    .peo-comp-row {
      display: grid;
      grid-template-columns: 1.6fr 1fr 1fr 1fr 1.2fr;
      gap: 0;
    }
    .peo-comp-row-highlight {
      display: grid;
      grid-template-columns: 1.6fr 1fr 1fr 1fr 1.2fr;
      gap: 0;
      background: #eef2ff;
      border-radius: 8px;
    }
    .peo-cell {
      padding: 12px 14px;
      font-size: 13px;
      color: #475569;
      border-bottom: 1px solid #e2e8f0;
      font-family: 'Libre Franklin', sans-serif;
    }
    .peo-hero-section {
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      text-align: center;
      overflow: hidden;
    }
    .peo-stats-bar {
      position: relative;
      width: 100%;
      margin-top: 32px;
      background: rgba(255,255,255,0.07);
      border-top: 1px solid rgba(255,255,255,0.12);
      backdrop-filter: blur(16px);
      padding: 18px 24px;
      box-sizing: border-box;
    }
    @media (max-width: 768px) {
      .peo-comp-wrapper { overflow-x: visible; }
      .peo-comp-header { display: none; }
      .peo-comp-row,
      .peo-comp-row-highlight {
        display: block;
        margin-bottom: 12px;
        border: 1px solid #e2e8f0;
        border-radius: 10px;
        overflow: hidden;
        background: #fff;
      }
      .peo-comp-row-highlight { background: #eef2ff; }
      .peo-cell {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 12px;
        border-bottom: 1px solid #f1f5f9;
        padding: 10px 14px;
      }
      .peo-cell:last-child { border-bottom: none; }
      .peo-cell::before {
        content: attr(data-label);
        font-weight: 700;
        color: #1e293b;
        font-size: 12px;
        min-width: 100px;
        flex-shrink: 0;
        font-family: 'Libre Franklin', sans-serif;
      }
      .peo-stats-bar { margin-top: 24px; }
      .peo-stats-bar .stats-inner { gap: 20px !important; }
    }
  `;

  const coaches = [
    {
      name: 'Emit',
      icon: '📖',
      tag: 'Biblical Wisdom Coach',
      color: T.accent,
      gradient: 'linear-gradient(135deg, #4338ca, #6366f1)',
      bg: T.accentLight,
      border: T.accentBorder,
      peoValue: 'Addresses existential burnout and purpose loss — the invisible driver behind your client companies\' most expensive resignations.',
      desc: 'Daily Biblical wisdom coaching through the Strategy 13 framework. Personalized scripture-rooted guidance for purpose clarity, stress, and life decisions.',
    },
    {
      name: 'SAFE Coach',
      icon: '🛡️',
      tag: 'Emotional Wellness — 24/7',
      color: T.red,
      gradient: 'linear-gradient(135deg, #dc2626, #ef4444)',
      bg: T.redLight,
      border: T.redBorder,
      peoValue: 'Fills the gap your EAP portfolio leaves open every evening and weekend. The #1 employee complaint about EAPs is availability — FHO removes that barrier entirely.',
      desc: 'Scripture-grounded emotional support with mood tracking, anxiety reduction, and crisis de-escalation. No session limits. No scheduling barrier.',
    },
    {
      name: 'Family Coach',
      icon: '👨‍👩‍👧‍👦',
      tag: 'Family Stress → Workplace Cost',
      color: T.green,
      gradient: 'linear-gradient(135deg, #047857, #10b981)',
      bg: T.greenLight,
      border: T.greenBorder,
      peoValue: 'Family stress is the #1 hidden absenteeism driver across your client base. Offering a benefit that addresses it gives your PEO portfolio a dimension no competitor has.',
      desc: 'Developmental stage-aware guidance for parenting, marriage communication, and family dynamics — available whenever family challenges emerge.',
    },
    {
      name: 'Mental Performance',
      icon: '⚡',
      tag: 'High-Performer Retention',
      color: T.red,
      gradient: 'linear-gradient(135deg, #b91c1c, #ef4444)',
      bg: T.redLight,
      border: T.redBorder,
      peoValue: 'Your client companies\' highest performers are their highest burnout risk. Faith-driven peak performance coaching keeps their best people from becoming their most expensive exits.',
      desc: 'CHAMP methodology for professionals, healthcare workers, first responders, and creatives. Sustainable performance — not temporary motivation.',
    },
  ];

  const peoPainPoints = [
    {
      icon: '📦',
      title: 'Your Benefits Portfolio Looks Like Everyone Else\'s',
      stat: '~Same',
      statLabel: 'EAP in every PEO portfolio',
      body: 'Every major PEO offers health insurance, 401(k), and an EAP nobody uses. FHO gives your portfolio a faith-grounded, AI-powered wellness benefit your competitors cannot replicate — a genuine differentiator in a commoditized space.',
    },
    {
      icon: '👻',
      title: 'The EAP You\'re Bundling Is a Ghost Town',
      stat: '3–6%',
      statLabel: 'average EAP utilization across your client base',
      body: 'You\'re selling a wellness benefit to your clients that their employees will almost never touch. Low utilization reflects on your portfolio\'s value. FHO is designed to be used — 24/7, no scheduling, no stigma, built for the faith-oriented majority.',
    },
    {
      icon: '🔄',
      title: 'Client Retention Depends on Benefits Depth',
      stat: '73%',
      statLabel: 'of SMBs cite benefits quality in PEO selection',
      body: 'Small and mid-sized businesses choose and stay with a PEO largely based on the quality and breadth of benefits they can offer their employees. A differentiated wellness benefit strengthens the reason to stay.',
    },
    {
      icon: '🌍',
      title: 'Your Clients\' Workforce Is More Faith-Oriented Than Their Benefits',
      stat: '65%',
      statLabel: 'of U.S. employees are faith-oriented',
      body: 'The majority of the U.S. workforce identifies as faith-oriented. Almost no benefits product is built for them. FHO is the first platform that is — while remaining fully inclusive for everyone else.',
    },
  ];


  const trustCards = [
    {
      icon: '🚀',
      iconBg: T.accentLight,
      title: 'Zero Operational Lift for You or Your Clients',
      body: 'FHO uses a fully employee-driven onboarding process. No IT integration. No HRIS dependency. No helpdesk tickets. Your client HR admins share one access link — their employees are active in under two minutes. This is a benefit you can add to your portfolio without adding overhead.',
      bullets: null,
    },
    {
      icon: '🛡️',
      iconBg: T.greenLight,
      title: 'Upstream of HIPAA — Clean Compliance Story',
      body: 'FHO operates as a wellness coaching platform, not a clinical service. We don\'t collect Protected Health Information — which means we operate upstream of HIPAA\'s covered entity rules. No diagnoses. No treatment records. No insurance data. This is not a clinical platform — it\'s a wellness benefit with a clean, simple compliance profile.',
      bullets: null,
    },
    {
      icon: '🔒',
      iconBg: T.amberLight,
      title: 'Encrypted. Certified. Authenticated.',
      body: 'Employee conversations are encrypted in transit, stored on SOC 2 certified infrastructure, and tied to authenticated accounts only. No data shared across accounts or accessible without authentication.',
      bullets: [
        ['🔐', 'TLS/HTTPS encryption on all data in transit'],
        ['🏢', 'DigitalOcean SOC 2 Type II certified data centers'],
        ['👤', 'Authenticated accounts — no anonymous data access'],
        ['🗑️', 'Employee data deletion available on request'],
      ],
    },
  ];

  const compRows = [
    { label: 'FHO Platform', highlight: true,  availability: '24/7 — no scheduling', utilization: '65%+ audience fit', faithDimension: '✅ Built-in', price: '$15–$20/emp/mo', lift: 'Zero — self-serve' },
    { label: 'Traditional EAP', highlight: false, availability: '3–6 week wait times', utilization: '3–6% industry avg', faithDimension: '❌ Secular only', price: '$25–$50+/emp/mo', lift: 'HR setup required' },
    { label: 'Generic Wellness App', highlight: false, availability: 'On-demand', utilization: '8–12% avg', faithDimension: '❌ Not present', price: '$10–$30/emp/mo', lift: 'App install required' },
    { label: 'Chaplaincy Services', highlight: false, availability: 'Scheduled only', utilization: 'Low — stigma & access', faithDimension: '✅ Faith-centered', price: '$35–$60+/emp/mo', lift: 'Vendor coordination' },
  ];

  return (
    <div style={{ background: T.pageBg, color: T.body, minHeight: '100vh', fontFamily: "'Source Serif 4', Georgia, serif" }}>

      <style>{mobileCSS}</style>

      {/* ══════════ HERO ══════════ */}
      <section
        className="peo-hero-section"
        style={{ minHeight: '90vh', background: T.heroBg, padding: '48px 24px 0' }}
      >
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)', backgroundSize: '60px 60px', zIndex: 0 }} />
        <div style={{ position: 'absolute', top: '-10%', right: '-5%', width: '55%', height: '70%', background: 'radial-gradient(ellipse, rgba(79,70,229,0.18) 0%, transparent 70%)', zIndex: 0 }} />
        <div style={{ position: 'absolute', bottom: '5%', left: '-5%', width: '40%', height: '50%', background: 'radial-gradient(ellipse, rgba(5,150,105,0.14) 0%, transparent 70%)', zIndex: 0 }} />

        <div style={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', padding: '8px 16px', borderRadius: '30px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)' }}>
          <svg width='36' height='36' viewBox='0 0 36 36' fill='none' xmlns='http://www.w3.org/2000/svg' style={{flexShrink:0}}>
            <circle cx='18' cy='18' r='16' stroke='#059669' strokeWidth='2.5' fill='none'/>
            <path d='M18 8 A10 10 0 1 1 8 18' stroke='#ef4444' strokeWidth='2.5' strokeLinecap='round' fill='none'/>
            <circle cx='18' cy='18' r='4' fill='#4f46e5'/>
          </svg>
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: '12px', fontWeight: '700', letterSpacing: '0.06em', fontFamily: "'Libre Franklin', sans-serif" }}><span style={{color:'#4f46e5'}}>F</span><span style={{color:'#ef4444'}}>H</span><span style={{color:'#059669'}}>O</span><span style={{color:'rgba(255,255,255,0.9)'}}> PLATFORM</span></div>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.55)', letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: "'Libre Franklin', sans-serif" }}>For Professional Employer Organizations</div>
          </div>
        </div>

        <div style={{ position: 'relative', zIndex: 1, maxWidth: '900px' }}>
          <h1 style={{ fontSize: 'clamp(2.4rem, 5.5vw, 4rem)', fontWeight: '700', lineHeight: 1.08, margin: '0 0 28px', color: '#fff', letterSpacing: '-0.02em' }}>
            The Benefit That Differentiates<br />
            <span style={{ color: '#a5b4fc' }}>Your Portfolio From Every Competitor.</span>
          </h1>
          <div style={{ marginBottom: '24px' }}>
            <SuperPhilosophyTagline variant="partnership" color="#fff" />
          </div>
          <p style={{ fontSize: 'clamp(1rem, 2vw, 1.2rem)', lineHeight: 1.75, color: 'rgba(255,255,255,0.8)', maxWidth: '680px', margin: '0 auto 18px', fontFamily: "'Libre Franklin', sans-serif", fontWeight: '400' }}>
            4 AI coaches. $15–$20 per employee per month. 24/7 availability. Self-onboarding in under two minutes. Zero IT lift. No HIPAA complexity. Faith-grounded — built for the workforce majority every EAP ignores.
          </p>
          <p style={{ fontSize: 'clamp(0.9rem, 1.5vw, 1rem)', lineHeight: 1.7, color: 'rgba(255,255,255,0.55)', maxWidth: '560px', margin: '0 auto 44px', fontFamily: "'Libre Franklin', sans-serif" }}>
            One partnership. Deployed across your entire client base. No new overhead for your team or theirs.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
            <button onClick={() => scrollTo('contact')}
              style={{ padding: '15px 34px', borderRadius: '10px', border: 'none', background: '#fff', color: T.navy, fontSize: '15px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 28px rgba(0,0,0,0.18)', transition: 'all 0.2s', fontFamily: "'Libre Franklin', sans-serif", letterSpacing: '0.01em' }}
              onMouseOver={e => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 8px 36px rgba(0,0,0,0.24)'; }}
              onMouseOut={e => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 4px 28px rgba(0,0,0,0.18)'; }}>
              Schedule a Partnership Call
            </button>
            <button onClick={() => document.getElementById('quote-tool')?.scrollIntoView({ behavior: 'smooth' })}
              style={{ padding: '15px 34px', borderRadius: '10px', background: 'transparent', border: '1px solid rgba(255,255,255,0.35)', color: '#fff', fontSize: '15px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s', fontFamily: "'Libre Franklin', sans-serif" }}
              onMouseOver={e => { e.target.style.borderColor = 'rgba(255,255,255,0.65)'; e.target.style.background = 'rgba(255,255,255,0.08)'; }}
              onMouseOut={e => { e.target.style.borderColor = 'rgba(255,255,255,0.35)'; e.target.style.background = 'transparent'; }}>
              View PMPM Pricing →
            </button>
              <button onClick={() => window.open('https://freehappyone.com/', '_blank')} style={{ padding: '14px 32px', borderRadius: '10px', background: 'transparent', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', fontSize: '16px', fontWeight: '600', cursor: 'pointer', transition: 'border-color 0.2s, background 0.2s' }}
                onMouseOver={e => { e.target.style.borderColor = 'rgba(255,255,255,0.7)'; e.target.style.background = 'rgba(255,255,255,0.1)'; }}
                onMouseOut={e => { e.target.style.borderColor = 'rgba(255,255,255,0.4)'; e.target.style.background = 'transparent'; }}>
                Visit FHO Platform →
              </button>
          </div>
        </div>

        <div className="peo-stats-bar">
          <div className="stats-inner" style={{ display: 'flex', justifyContent: 'center', gap: 'clamp(24px, 5vw, 64px)', flexWrap: 'wrap' }}>
            {[
              ['65%',    'Workforce is faith-oriented'],
              ['3–6%',   'Average EAP utilization'],
              ['$15–$20','Per employee / month'],
              ['4',      'Specialized AI coaches'],
              ['< 2 min','Employee onboarding'],
            ].map(([num, lbl]) => (
              <div key={lbl} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '22px', fontWeight: '700', color: '#fff', letterSpacing: '-0.01em' }}>{num}</div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.55)', letterSpacing: '0.04em', fontFamily: "'Libre Franklin', sans-serif", marginTop: '2px' }}>{lbl}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <LaunchBanner />

      {/* ══════════ PEO PAIN POINTS ══════════ */}
      <section id="pain" ref={setSectionRef('pain')} style={{ padding: '72px 24px', background: T.sectionAlt }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', ...fadeIn('pain') }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <div style={{ display: 'inline-block', padding: '4px 14px', borderRadius: '16px', marginBottom: '16px', background: T.redLight, border: `1px solid ${T.redBorder}`, fontSize: '11px', fontWeight: '700', color: '#b91c1c', letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: "'Libre Franklin', sans-serif" }}>The PEO Reality</div>
            <h2 style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.7rem)', fontWeight: '700', margin: '0 0 18px', color: T.heading, letterSpacing: '-0.02em' }}>Four Problems Every PEO<br />Needs to Solve Right Now</h2>
            <p style={{ fontSize: '1.1rem', color: T.body, maxWidth: '640px', margin: '0 auto', lineHeight: 1.7, fontFamily: "'Libre Franklin', sans-serif" }}>
              And the one platform purpose-built to address all of them — without adding complexity to your portfolio.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px' }}>
            {peoPainPoints.map((p, i) => (
              <div key={p.title} style={{ ...fadeIn('pain', i * 0.1), background: T.cardBg, borderRadius: '16px', padding: '28px 24px', border: `1px solid ${T.cardBorder}`, boxShadow: '0 2px 16px rgba(0,0,0,0.05)' }}>
                <div style={{ fontSize: '28px', marginBottom: '14px' }}>{p.icon}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '28px', fontWeight: '700', color: T.red, lineHeight: 1, letterSpacing: '-0.02em' }}>{p.stat}</span>
                  <span style={{ fontSize: '11px', color: T.bodyLight, fontFamily: "'Libre Franklin', sans-serif", letterSpacing: '0.03em' }}>{p.statLabel}</span>
                </div>
                <h3 style={{ fontSize: '15px', fontWeight: '700', color: T.heading, margin: '0 0 10px', lineHeight: 1.35 }}>{p.title}</h3>
                <p style={{ fontSize: '13px', color: T.body, lineHeight: 1.7, margin: 0, fontFamily: "'Libre Franklin', sans-serif" }}>{p.body}</p>
              </div>
            ))}
          </div>

          <div style={{ marginTop: '28px', padding: '22px 32px', borderRadius: '12px', background: T.accentLight, border: `1px solid ${T.accentBorder}`, textAlign: 'center' }}>
            <span style={{ fontSize: '1.05rem', fontWeight: '700', color: T.accent }}>FHO is the only platform that addresses all four — </span>
            <span style={{ fontSize: '1.05rem', color: T.body, fontFamily: "'Libre Franklin', sans-serif" }}>in one benefit, at one price, with zero operational lift for your team or your clients.</span>
          </div>
        </div>
      </section>

      {/* ══════════ FOUR COACHES ══════════ */}
      <section id="coaches" ref={setSectionRef('coaches')} style={{ padding: '72px 24px', background: T.pageBg }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', ...fadeIn('coaches') }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <div style={{ display: 'inline-block', padding: '4px 14px', borderRadius: '16px', marginBottom: '16px', background: T.accentLight, border: `1px solid ${T.accentBorder}`, fontSize: '11px', fontWeight: '700', color: T.accent, letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: "'Libre Franklin', sans-serif" }}>The Platform</div>
            <h2 style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.7rem)', fontWeight: '700', margin: '0 0 18px', color: T.heading, letterSpacing: '-0.02em' }}>Four Coaches. One Platform.<br />One Line Item in Your Benefits Portfolio.</h2>
            <p style={{ fontSize: '1.1rem', color: T.body, maxWidth: '680px', margin: '0 auto', lineHeight: 1.7, fontFamily: "'Libre Franklin', sans-serif" }}>
              Each employee chooses their default coach at first login. Every coach stays accessible from one account. One benefit. Every dimension of your clients' workforce covered.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))', gap: '18px', marginBottom: '28px' }}>
            {coaches.map((c, i) => (
              <div key={c.name} style={{ ...fadeIn('coaches', i * 0.08), background: T.cardBg, borderRadius: '16px', border: `1px solid ${T.cardBorder}`, overflow: 'hidden', boxShadow: '0 2px 16px rgba(0,0,0,0.05)' }}>
                <div style={{ height: '6px', background: c.gradient }} />
                <div style={{ padding: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                    <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: c.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 }}>{c.icon}</div>
                    <div>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: T.heading }}>{c.name}</div>
                      <div style={{ fontSize: '11px', color: c.color, fontWeight: '600', fontFamily: "'Libre Franklin', sans-serif", letterSpacing: '0.04em' }}>{c.tag}</div>
                    </div>
                  </div>
                  <p style={{ fontSize: '13px', color: T.body, lineHeight: 1.65, margin: '0 0 16px', fontFamily: "'Libre Franklin', sans-serif" }}>{c.desc}</p>
                  <div style={{ padding: '12px 14px', borderRadius: '10px', background: c.bg, border: `1px solid ${c.border}` }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: c.color, letterSpacing: '0.05em', marginBottom: '4px', fontFamily: "'Libre Franklin', sans-serif" }}>PEO PORTFOLIO VALUE</div>
                    <p style={{ fontSize: '12px', color: T.heading, lineHeight: 1.6, margin: 0, fontFamily: "'Libre Franklin', sans-serif" }}>{c.peoValue}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ COMPARISON TABLE ══════════ */}
      <section id="compare" ref={setSectionRef('compare')} style={{ padding: '72px 24px', background: T.sectionAlt }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', ...fadeIn('compare') }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <div style={{ display: 'inline-block', padding: '4px 14px', borderRadius: '16px', marginBottom: '16px', background: T.accentLight, border: `1px solid ${T.accentBorder}`, fontSize: '11px', fontWeight: '700', color: T.accent, letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: "'Libre Franklin', sans-serif" }}>How We Compare</div>
            <h2 style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.7rem)', fontWeight: '700', margin: '0 0 18px', color: T.heading, letterSpacing: '-0.02em' }}>FHO vs. What Your Portfolio<br />Already Offers</h2>
            <p style={{ fontSize: '1.1rem', color: T.body, maxWidth: '640px', margin: '0 auto', lineHeight: 1.7, fontFamily: "'Libre Franklin', sans-serif" }}>
              See why FHO fills a gap that no current offering in the standard PEO benefits stack addresses.
            </p>
          </div>

          <div className="peo-comp-wrapper">
            <div className="peo-comp-header" style={{ borderRadius: '10px 10px 0 0', overflow: 'hidden', marginBottom: '2px' }}>
              {['Platform', 'Availability', 'Utilization', 'Faith Dimension', 'Price / Mo'].map((h) => (
                <div key={h} className="peo-cell" style={{ background: T.navy, color: '#fff', fontWeight: '700', fontSize: '13px', fontFamily: "'Libre Franklin', sans-serif", borderBottom: 'none', padding: '14px' }}>{h}</div>
              ))}
            </div>
            {compRows.map((row) => (
              <div key={row.label} className={row.highlight ? 'peo-comp-row-highlight' : 'peo-comp-row'} style={{ marginBottom: '2px' }}>
                <div className="peo-cell" data-label="Platform" style={{ fontWeight: row.highlight ? '700' : '400', color: row.highlight ? T.accent : T.heading, fontFamily: "'Libre Franklin', sans-serif" }}>
                  {row.highlight ? '⭐ ' : ''}{row.label}
                </div>
                <div className="peo-cell" data-label="Availability" style={{ fontFamily: "'Libre Franklin', sans-serif" }}>{row.availability}</div>
                <div className="peo-cell" data-label="Utilization" style={{ fontFamily: "'Libre Franklin', sans-serif" }}>{row.utilization}</div>
                <div className="peo-cell" data-label="Faith Dimension" style={{ fontFamily: "'Libre Franklin', sans-serif" }}>{row.faithDimension}</div>
                <div className="peo-cell" data-label="Price / Mo" style={{ fontFamily: "'Libre Franklin', sans-serif" }}>{row.price}</div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: '24px', padding: '18px 24px', borderRadius: '10px', background: T.accentLight, border: `1px solid ${T.accentBorder}`, textAlign: 'center' }}>
            <span style={{ fontSize: '1rem', fontWeight: '700', color: T.accent }}>FHO is the only option with a built-in faith dimension, 24/7 availability, and utilization designed for the 65% majority — </span>
            <span style={{ fontSize: '1rem', color: T.body, fontFamily: "'Libre Franklin', sans-serif" }}>at a price point that fits any client tier in your book.</span>
          </div>
        </div>
      </section>

      {/* ══════════ TRUST / COMPLIANCE ══════════ */}
      <section id="trust" ref={setSectionRef('trust')} style={{ padding: '72px 24px', background: T.pageBg }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', ...fadeIn('trust') }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <div style={{ display: 'inline-block', padding: '4px 14px', borderRadius: '16px', marginBottom: '16px', background: T.greenLight, border: `1px solid ${T.greenBorder}`, fontSize: '11px', fontWeight: '700', color: T.green, letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: "'Libre Franklin', sans-serif" }}>Built for Your Portfolio</div>
            <h2 style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.7rem)', fontWeight: '700', margin: '0 0 18px', color: T.heading, letterSpacing: '-0.02em' }}>Easy to Add.<br />Easier to Keep.</h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            {trustCards.map((card, i) => (
              <div key={card.title} style={{ ...fadeIn('trust', i * 0.1), background: T.cardBg, borderRadius: '16px', padding: '28px 24px', border: `1px solid ${T.cardBorder}`, boxShadow: '0 2px 16px rgba(0,0,0,0.05)' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: card.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', marginBottom: '18px' }}>{card.icon}</div>
                <h3 style={{ fontSize: '17px', fontWeight: '700', color: T.heading, margin: '0 0 12px', lineHeight: 1.35 }}>{card.title}</h3>
                <p style={{ fontSize: '13px', color: T.body, lineHeight: 1.7, margin: '0 0 16px', fontFamily: "'Libre Franklin', sans-serif" }}>{card.body}</p>
                {card.bullets && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {card.bullets.map(([ico, txt]) => (
                      <div key={txt} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                        <span style={{ fontSize: '16px', flexShrink: 0 }}>{ico}</span>
                        <span style={{ fontSize: '12px', color: T.body, lineHeight: 1.6, fontFamily: "'Libre Franklin', sans-serif" }}>{txt}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ PARTNERSHIP MODEL ══════════ */}
      <section id="model" ref={setSectionRef('model')} style={{ padding: '72px 24px', background: T.sectionAlt }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', ...fadeIn('model') }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <div style={{ display: 'inline-block', padding: '4px 14px', borderRadius: '16px', marginBottom: '16px', background: T.amberLight, border: `1px solid ${T.amberBorder}`, fontSize: '11px', fontWeight: '700', color: T.amber, letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: "'Libre Franklin', sans-serif" }}>Partnership Model</div>
            <h2 style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.7rem)', fontWeight: '700', margin: '0 0 18px', color: T.heading, letterSpacing: '-0.02em' }}>How This Works for Your PEO</h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
            {[
              { step: '01', title: 'Portfolio Add-On', color: T.accent, bg: T.accentLight, border: T.accentBorder, body: 'FHO is added to your benefits portfolio as a recommended wellness benefit. You offer it alongside your existing stack. Your benefits advisors present it to client HR contacts during open enrollment, onboarding, or portfolio reviews.' },
              { step: '02', title: 'Revenue Share', color: T.green, bg: T.greenLight, border: T.greenBorder, body: 'FHO pays a revenue share on every active account referred through your PEO partnership. Monthly recurring. Transparent reporting. The more of your clients\' employees activate, the more you earn.' },
              { step: '03', title: 'Client Activation', color: T.amber, bg: T.amberLight, border: T.amberBorder, body: 'Your client HR admin receives one access link. Employees self-onboard in under 2 minutes. No IT project. No implementation call. No new software for you to support. FHO handles all onboarding and employee support.' },
            ].map((s) => (
              <div key={s.step} style={{ background: T.cardBg, borderRadius: '16px', padding: '28px 24px', border: `1px solid ${T.cardBorder}`, boxShadow: '0 2px 16px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: s.bg, border: `1px solid ${s.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', fontWeight: '800', color: s.color, fontFamily: "'Libre Franklin', sans-serif", flexShrink: 0 }}>{s.step}</div>
                  <h3 style={{ fontSize: '17px', fontWeight: '700', color: T.heading, margin: 0 }}>{s.title}</h3>
                </div>
                <p style={{ fontSize: '13px', color: T.body, lineHeight: 1.7, margin: 0, fontFamily: "'Libre Franklin', sans-serif" }}>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ VIDEO ══════════ */}
      <section id="video" ref={setSectionRef('video')} style={{ padding: '72px 24px', background: T.sectionAlt }}>
        <div style={{ maxWidth: '860px', margin: '0 auto', ...fadeIn('video') }}>
          <div style={{ textAlign: 'center', marginBottom: '28px' }}>
            <h2 style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.4rem)', fontWeight: '700', margin: '0 0 12px', color: T.heading, letterSpacing: '-0.02em', fontFamily: "'Libre Franklin', sans-serif" }}>See the Member Experience</h2>
            <p style={{ fontSize: '1rem', color: T.bodyLight, margin: 0, fontFamily: "'Libre Franklin', sans-serif" }}>How an employee goes from access link to first coaching session — in under two minutes.</p>
          </div>
          <div style={{ borderRadius: '16px', overflow: 'hidden', marginBottom: '28px', boxShadow: '0 4px 32px rgba(0,0,0,0.10)' }}>
            <video controls style={{ width: '100%', display: 'block' }}>
              <source src="/videos/peo-partnership.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          <div style={{ padding: '26px 32px', borderRadius: '12px', background: T.accentLight, borderLeft: `4px solid ${T.accent}` }}>
            <p style={{ fontSize: '1.05rem', color: T.heading, lineHeight: 1.75, fontStyle: 'italic', margin: 0, fontFamily: "'Libre Franklin', sans-serif" }}>
              "One PEO partnership gives FHO distribution across hundreds of client companies simultaneously. The member experience is the proof of concept — no technical setup, no scheduling friction, just value from minute one."
            </p>
          </div>
        </div>
      </section>

      {/* ══════════ QUOTE TOOL ══════════ */}
      <section style={{ padding: '72px 24px', background: T.pageBg }}>
        <div style={{ maxWidth: '760px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <h2 style={{ fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', fontWeight: '700', color: T.heading, margin: '0 0 12px', letterSpacing: '-0.02em' }}>Get Your Instant Partnership Quote</h2>
            <p style={{ fontSize: '1rem', color: T.body, lineHeight: 1.7, margin: 0, fontFamily: "'Libre Franklin', sans-serif" }}>Pricing is customized based on your organization's size and structure. Fill out the form below for an instant estimate.</p>
          </div>
          <QuoteTool partnershipType="peo" />
        </div>
      </section>

      {/* ══════════ CTA ══════════ */}
      <section id="contact" ref={setSectionRef('contact')} style={{ padding: '72px 24px', background: T.ctaBg }}>
        <div style={{ maxWidth: '760px', margin: '0 auto', textAlign: 'center', ...fadeIn('contact') }}>
          <div style={{ display: 'inline-block', padding: '4px 14px', borderRadius: '16px', marginBottom: '16px', background: T.accentLight, border: `1px solid ${T.accentBorder}`, fontSize: '11px', fontWeight: '700', color: T.accent, letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: "'Libre Franklin', sans-serif" }}>Get Started</div>
          <h2 style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.7rem)', fontWeight: '700', margin: '0 0 18px', color: T.heading, letterSpacing: '-0.02em' }}>Let's Build Something<br />Your Clients Can't Get Anywhere Else.</h2>
          <p style={{ fontSize: '1.1rem', color: T.body, lineHeight: 1.7, margin: '0 auto 40px', maxWidth: '560px', fontFamily: "'Libre Franklin', sans-serif" }}>
            Schedule a 30-minute portfolio demo. We'll walk your benefits team through the platform, the partnership model, and what client activation looks like — no technical setup required.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
            <a href="mailto:partnerships@freehappyone.com"
              style={{ padding: '16px 36px', borderRadius: '10px', background: T.accent, color: '#fff', fontSize: '15px', fontWeight: '700', textDecoration: 'none', boxShadow: '0 4px 20px rgba(79,70,229,0.3)', transition: 'all 0.2s', fontFamily: "'Libre Franklin', sans-serif", display: 'inline-block' }}>
              Schedule a Partnership Call
            </a>
            <button onClick={() => document.getElementById('quote-tool')?.scrollIntoView({ behavior: 'smooth' })}
              style={{ padding: '16px 36px', borderRadius: '10px', background: T.greenLight, color: T.green, fontSize: '15px', fontWeight: '700', border: `1px solid ${T.greenBorder}`, cursor: 'pointer', transition: 'all 0.2s', fontFamily: "'Libre Franklin', sans-serif' " }}>
              Get an Instant Quote →
            </button>
          </div>
          <p style={{ marginTop: '32px', fontSize: '13px', color: T.bodyLight, fontFamily: "'Libre Franklin', sans-serif" }}>
            MindStreams Technology · freehappyone.com
          </p>
        </div>
      </section>

    </div>
  );
};

export default PEOPartnership;
